import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { consoleCopy, threadCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Rule, SidePanel } from '@/ui/Console/Console'
import { Drawer } from '@/ui/Drawer/Drawer'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Input } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Link } from '@/ui/Link/Link'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { Message } from '@/ui/Message/Message'
import { messagesApi } from '../../api/messages'
import { resourcesFor } from '../../api/resources'
import { ROUTES } from '../../constants/routes'
import { useThreadsQuery } from '../../hooks/queries'
import { useAutoScroll } from '../../hooks/useAutoScroll'
import {
  type ActivityEntry,
  streamKey,
  useStreamStore,
} from '../../store/stream'
import { useThreadTitleFlash } from '../../store/threadTitle'
import { useToastStore } from '../../store/toast'
import { useWorkspaceStore } from '../../store/workspace'
import type {
  AgentParticipant,
  HistoricalMessage,
  ThreadSummary,
  TopicResource,
} from '../../types/api'
import { RefreshFailed } from '../RefreshFailed'
import { ResourceCard } from './ResourceCard'
import { ThreadPanel } from './ThreadPanel'
import styles from './ThreadViewPane.module.css'
import { authorView, resolveParticipants } from './tags'
import { buildTimeline } from './timeline'

// Module-level stable empties. Returning `[]` inline from a zustand selector
// breaks useSyncExternalStore (new reference every call → infinite re-render /
// React #185), so default to these constants when the thread has no live data.
const NO_AGENTS: string[] = []
const NO_ACTIVITY: ActivityEntry[] = []
// Default prop values must also be reference-stable or useCallback/memo
// comparisons below churn on every render.
const NO_USERS: string[] = []
const NO_MESSAGES: HistoricalMessage[] = []
// Joins the live covered-resource ids into one primitive so the zustand
// selector's result only changes identity when the covered set changes.
const COVERED_ID_SEP = '\u0000'

interface Props {
  topicId: string
  threadId: string
  workspaceId?: string
  agents: AgentParticipant[]
  users?: string[]
  currentUser: string | null
  /** Topic display name (panel scope bands). */
  topicName?: string
  onClose: () => void
  onResolve: () => void
  onReopen: () => void
  onArchive: () => void
  /** Send a follow-up asking the agent to resume a length-truncated reply. */
  onContinue: () => void
  /** Console-v2: open the topic's resources surface (panel footer link). */
  onOpenTopicResources?: () => void
}

/** The zoomed thread: full-width, replaces the list. Esc returns. */
export function ThreadViewPane({
  topicId,
  threadId,
  workspaceId,
  agents,
  users = NO_USERS,
  currentUser,
  topicName,
  onClose,
  onResolve,
  onReopen,
  onArchive,
  onContinue,
  onOpenTopicResources,
}: Props) {
  const key = streamKey(topicId, threadId)
  const navigate = useNavigate()
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId)
  const resolvedWorkspaceId = workspaceId ?? activeWorkspaceId ?? ''
  const { data: threadData } = useThreadsQuery(topicId)
  const summary: ThreadSummary | undefined = threadData?.threads.find(
    (t) => t.thread_id === threadId,
  )

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['thread', topicId, threadId],
    queryFn: () => messagesApi.thread(topicId, threadId, { limit: 200 }),
    staleTime: 5_000,
  })
  const threadResQuery = useQuery({
    queryKey: ['thread-resources', topicId, threadId],
    queryFn: () => messagesApi.threadResources(topicId, threadId),
    staleTime: 10_000,
  })
  const { data: topicRes } = useQuery({
    queryKey: ['resources', 'topic', topicId],
    queryFn: () => resourcesFor({ kind: 'topic', id: topicId }).list(),
    staleTime: 10_000,
  })

  // Live presence + activity for this thread.
  const activeAgents = useStreamStore((s) => s.activeTurns[key] ?? NO_AGENTS)
  const truncated = useStreamStore((s) => s.truncatedKeys[key] === true)
  const flashAt = useThreadTitleFlash((s) => (s.flashAt[threadId] ? 1 : 0))
  // Live covered-resource ids for this thread, flattened into one primitive:
  // the selector still runs per store update, but the pane only re-renders
  // when a covered card actually appears/disappears — not on every streamed
  // chunk. (The scan stays cheap: appendActivity trims each agent's log at
  // turn boundaries, so logs hold roughly the current turn only.)
  const coveredLiveIds = useStreamStore((s) => {
    const log = s.activityLog[topicId]
    if (!log) return ''
    let ids = ''
    for (const entries of Object.values(log)) {
      for (const e of entries) {
        if (e.type === 'resource_card' && e.data.thread_id === threadId) {
          const id = e.data.resource_id ? String(e.data.resource_id) : ''
          if (id) ids = ids ? `${ids}${COVERED_ID_SEP}${id}` : id
        }
      }
    }
    return ids
  })

  // Inline title editing → pins a user-set title (stops auto-retitling).
  const qc = useQueryClient()
  const addToast = useToastStore((s) => s.addToast)
  const titleId = useId()
  const [detailsOpen, setDetailsOpen] = useState(false)
  const showSkeleton = useSkeletonDelay(isLoading)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const titleInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (editing) titleInputRef.current?.focus()
  }, [editing])

  async function commitTitle() {
    const trimmed = draft.trim()
    setEditing(false)
    if (!trimmed || trimmed === (summary?.title ?? '')) return
    try {
      await messagesApi.pinThreadTitle(topicId, threadId, trimmed)
      void qc.invalidateQueries({ queryKey: ['threads', topicId] })
    } catch (err) {
      addToast(
        err instanceof Error ? err.message : threadCopy.view.renameFailed,
        'error',
      )
    }
  }

  const { scrollRef, isAtBottom, scrollToBottom } = useAutoScroll()

  // Reset scroll instantly on thread switches. Narrow deps: re-scroll only
  // when the thread changes, not when the (stable) hook identity refreshes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional narrow deps for auto-scroll reset
  useEffect(() => {
    scrollToBottom({ behavior: 'auto' })
  }, [threadId, scrollToBottom])

  const messages = data?.messages ?? NO_MESSAGES
  const parts = resolveParticipants(summary?.participants ?? [], agents)
  const agentCount = parts.filter((p) => p.isAgent).length

  // Resource ids whose review card is rendered attached to its tool call —
  // from completed turns (paired tool results in the transcript) and from the
  // live timeline (a review still pending). The standalone pointer-message
  // line is suppressed for these so the card never shows twice; it remains
  // the fallback for reload-mid-wait / crashed turns. Memoized so the Set is
  // reference-stable for the memoized MessageLine children below.
  const coveredResourceIds = useMemo(() => {
    const covered = new Set<string>()
    for (const m of messages) {
      for (const tc of m.tool_calls ?? []) {
        const id = reviewResourceIdFromToolCall(tc.name, tc.result)
        if (id) covered.add(id)
      }
    }
    if (coveredLiveIds) {
      for (const id of coveredLiveIds.split(COVERED_ID_SEP)) covered.add(id)
    }
    return covered
  }, [messages, coveredLiveIds])

  // Union of thread-specific and topic-level resources (by id to dedup),
  // this thread's scoped rows first — on the thread view they're the most
  // relevant, and the chip styling distinguishes them from shared ones.
  const resourceById = new Map<string, TopicResource>()
  for (const r of topicRes?.resources ?? []) resourceById.set(r.id, r)
  for (const r of threadResQuery.data?.resources ?? [])
    resourceById.set(r.id, r)
  const resources = [...resourceById.values()].sort(
    (a, b) => Number(b.thread_id != null) - Number(a.thread_id != null),
  )

  // Blocked-ness is the Phase-0 derived needs_you (server truth), not the
  // legacy free-text hint; the hint only personalizes the label.
  const blocked =
    !!summary?.needs_you &&
    summary.status !== 'resolved' &&
    activeAgents.length === 0
  const blockedAgentName = (() => {
    const id = summary?.participants.find((p) => p.kind === 'agent')?.display_id
    return (
      agents.find((a) => a.agent_id === id)?.name ??
      threadCopy.view.agentFallback
    )
  })()

  // Stable identity (with stable `users`/`agents` refs) keeps the memoized
  // MessageLine tree from re-rendering on unrelated pane renders.
  const handleMentionClick = useCallback(
    (mentionName: string) => {
      if (!resolvedWorkspaceId) return
      const normalized = mentionName.trim().toLowerCase()
      const agent = agents.find(
        (a) =>
          a.name.toLowerCase() === normalized ||
          a.agent_id.toLowerCase() === normalized,
      )
      if (agent) {
        void navigate(ROUTES.agentProfile(resolvedWorkspaceId, agent.agent_id))
      } else {
        const user = users.find((u) => u.toLowerCase() === normalized)
        if (user) {
          void navigate(ROUTES.personProfile(resolvedWorkspaceId, user))
        }
      }
    },
    [resolvedWorkspaceId, agents, users, navigate],
  )

  const panel = (
    <ThreadPanel
      topicId={topicId}
      topicName={topicName}
      summary={summary}
      threadResources={threadResQuery.data?.resources}
      resourcesError={threadResQuery.isError}
      onRetryResources={() => void threadResQuery.refetch()}
      messages={messages}
      agents={agents}
      workspaceId={resolvedWorkspaceId}
      onOpenTopicResources={() => onOpenTopicResources?.()}
    />
  )

  const countMeta = (n: number, word: (n: number) => string) => (
    <Text variant="caption" tone="text-mute" numeric>
      <Text as="span" variant="caption" tone="text-sub" numeric>
        {n}
      </Text>{' '}
      {word(n)}
    </Text>
  )

  return (
    <div className={styles.view}>
      <div className={styles.head}>
        <Stack gap="space-8" align="start">
          <Link
            href={ROUTES.topic(topicId)}
            onClick={(e) => {
              if (
                e.button === 0 &&
                !e.metaKey &&
                !e.ctrlKey &&
                !e.shiftKey &&
                !e.altKey
              ) {
                e.preventDefault()
                onClose()
              }
            }}
          >
            {threadCopy.view.allThreads}
          </Link>
          <Inline gap="space-12" align="start" className={styles.titleRow}>
            {editing ? (
              <Input
                ref={titleInputRef}
                id={titleId}
                aria-label={threadCopy.view.titleLabel}
                className={styles.title}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => void commitTitle()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void commitTitle()
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    setEditing(false)
                  }
                }}
              />
            ) : (
              <Text
                as="h1"
                variant="title"
                tone="text"
                id={titleId}
                key={flashAt}
                data-flash={flashAt ? '' : undefined}
                className={styles.title}
              >
                {summary?.title ?? threadCopy.view.titleFallback}
              </Text>
            )}
            <Inline gap="space-8">
              <Button
                variant="outline"
                onClick={() => {
                  setDraft(summary?.title ?? '')
                  setEditing(true)
                }}
              >
                {threadCopy.view.rename}
              </Button>
              <span className={styles.detailsTrigger}>
                <Button variant="outline" onClick={() => setDetailsOpen(true)}>
                  {threadCopy.view.details}
                </Button>
              </span>
              {summary?.status === 'resolved' ? (
                <Button variant="outline" onClick={onReopen}>
                  {threadCopy.view.reopen}
                </Button>
              ) : (
                <Button variant="outline" onClick={onResolve}>
                  {threadCopy.view.resolve}
                </Button>
              )}
              <Button variant="outline" onClick={onArchive}>
                {threadCopy.view.archive}
              </Button>
            </Inline>
          </Inline>
          <Inline gap="space-16">
            {countMeta(
              summary?.message_count ?? messages.length,
              threadCopy.view.messages,
            )}
            {countMeta(resources.length, threadCopy.view.resources)}
            {countMeta(agentCount, threadCopy.view.agents)}
            {summary?.title_pinned_by && (
              <Text variant="caption" tone="text-mute" numeric>
                {threadCopy.view.titledBy}{' '}
                <Text as="span" variant="caption" tone="text-sub" numeric>
                  {summary.title_pinned_by}
                </Text>
              </Text>
            )}
          </Inline>
        </Stack>
        <Rule />
      </div>

      <div className={styles.body}>
        <div className={styles.column}>
          {/* The log announces each settled turn once, when it is appended; a
            streaming turn renders after it, outside the live region
            (accessibility: a streaming run is not a live region). */}
          <div ref={scrollRef} className={styles.transcript}>
            <div
              role="log"
              aria-labelledby={titleId}
              aria-busy={isLoading || undefined}
              className={styles.log}
            >
              {isLoading &&
                showSkeleton &&
                [0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className={styles.skeletonTurn}
                    aria-hidden="true"
                  >
                    <Skeleton variant="circle" />
                    <Skeleton lines={2} />
                  </div>
                ))}
              {isError && !data && (
                <div className={styles.notice}>
                  <Alert tone="danger">
                    <Inline gap="space-8" align="baseline">
                      {threadCopy.view.loadFailed}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void refetch()}
                      >
                        {threadCopy.message.retry}
                      </Button>
                    </Inline>
                  </Alert>
                </div>
              )}
              {isError && data && (
                <div className={styles.notice}>
                  <RefreshFailed
                    message={threadCopy.view.loadFailed}
                    onRetry={() => void refetch()}
                  />
                </div>
              )}
              {!isLoading && !isError && messages.length === 0 && (
                <div className={styles.notice}>
                  <EmptyState
                    variant="inline"
                    description={threadCopy.view.empty}
                  />
                </div>
              )}
              {messages.map((m) => (
                <MessageTurn
                  key={`${m.timestamp}-${m.author.participant_id}-${m.role}`}
                  msg={m}
                  topicId={topicId}
                  workspaceId={resolvedWorkspaceId}
                  agents={agents}
                  currentUser={currentUser}
                  coveredResourceIds={coveredResourceIds}
                  onMentionClick={handleMentionClick}
                />
              ))}
              {blocked && (
                <div className={styles.notice}>
                  <Alert tone="warning">
                    {summary?.waiting_on === currentUser
                      ? threadCopy.view.waitingOnYou(blockedAgentName)
                      : threadCopy.view.waitingOn(
                          blockedAgentName,
                          summary?.waiting_on ?? '',
                        )}
                  </Alert>
                </div>
              )}
              {/* The last reply was cut off by the output-token limit — never let a
              truncated turn look complete. */}
              {truncated && activeAgents.length === 0 && (
                <div className={styles.notice}>
                  <Alert tone="warning">
                    <Inline gap="space-8" align="baseline">
                      {threadCopy.view.truncated}
                      <Button variant="outline" size="sm" onClick={onContinue}>
                        {threadCopy.view.continue}
                      </Button>
                    </Inline>
                  </Alert>
                </div>
              )}
            </div>
            {activeAgents.map((agentId) => {
              const name =
                agents.find((a) => a.agent_id === agentId)?.name ?? agentId
              return (
                <LiveTurn
                  key={`live-${agentId}`}
                  topicId={topicId}
                  threadId={threadId}
                  agentId={agentId}
                  name={name}
                  workspaceId={resolvedWorkspaceId}
                  onMentionClick={handleMentionClick}
                />
              )
            })}
          </div>
          {!isAtBottom && (
            <span className={styles.scrollLatest}>
              <Button
                variant="outline"
                size="icon"
                aria-label={threadCopy.view.scrollToLatest}
                onClick={() => scrollToBottom()}
              >
                <Icon name="chevron-down" />
              </Button>
            </span>
          )}
        </div>
        <div className={styles.panelSlot}>
          <SidePanel label={threadCopy.view.detailsTitle}>{panel}</SidePanel>
        </div>
      </div>

      <Drawer
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title={threadCopy.view.detailsTitle}
        side="inline-end"
      >
        {panel}
      </Drawer>
    </div>
  )
}

/**
 * Resource id a review tool call created, from its paired result — the
 * historical anchor for rendering the card attached to its tool call. Null for
 * non-review tools and unpaired/failed calls.
 */
function reviewResourceIdFromToolCall(
  name: string,
  result: unknown,
): string | null {
  if (name !== 'plan_submit' && name !== 'question_ask') return null
  const r = (result ?? {}) as Record<string, unknown>
  const id = r.plan_id ?? r.question_id ?? r.resource_id
  return id ? String(id) : null
}

function resourceStack(cards: ReactNode[]): ReactNode {
  return cards.length > 0 ? <Stack gap="space-8">{cards}</Stack> : undefined
}

// Memoized so the ~200-message transcript doesn't re-render on every pane
// render (live chunks elsewhere in the tree): props are reference-stable
// (react-query data, memoized covered set, useCallback'd handler).
const MessageTurn = memo(function MessageTurn({
  msg,
  topicId,
  workspaceId,
  agents,
  currentUser,
  coveredResourceIds,
  onMentionClick,
}: {
  msg: HistoricalMessage
  topicId: string
  workspaceId?: string
  agents: AgentParticipant[]
  currentUser: string | null
  /** Reviews already rendering a card elsewhere (tool-adjacent or live). */
  coveredResourceIds: Set<string>
  onMentionClick?: (mentionName: string) => void
}) {
  const av = authorView(msg, agents, currentUser)
  // The card message is a pointer; its card is normally rendered attached to
  // the review tool call (live timeline, or the paired tool call below). The
  // standalone card is only the fallback when nothing covers it (reload
  // mid-wait, crashed turn, paginated-out tool call).
  const standaloneCard =
    msg.resource_card && !coveredResourceIds.has(msg.resource_card.resource_id)
      ? msg.resource_card
      : undefined
  const hasContent = !!msg.content.trim() && !msg.resource_card

  const href = workspaceId
    ? av.isAgent
      ? ROUTES.agentProfile(workspaceId, av.id)
      : ROUTES.personProfile(
          workspaceId,
          av.name === consoleCopy.you
            ? (currentUser ?? av.id)
            : av.id || av.name,
        )
    : undefined

  const cards: ReactNode[] = []
  if (standaloneCard) {
    cards.push(
      <ResourceCard
        key="standalone"
        topicId={topicId}
        resourceId={standaloneCard.resource_id}
        fallbackTitle={standaloneCard.title}
      />,
    )
  }
  for (const tc of msg.tool_calls ?? []) {
    const cardId = reviewResourceIdFromToolCall(tc.name, tc.result)
    if (cardId) {
      cards.push(
        <ResourceCard key={tc.id} topicId={topicId} resourceId={cardId} />,
      )
    }
  }

  return (
    <Message
      author={{
        id: av.id,
        name: av.name,
        kind: av.isAgent ? 'agent' : 'human',
        href,
      }}
      timestamp={msg.timestamp}
      body={standaloneCard ? '' : hasContent ? msg.content : ''}
      reasoning={av.isAgent ? (msg.reasoning ?? undefined) : undefined}
      toolCalls={msg.tool_calls?.map((tc) => ({
        id: tc.id,
        name: tc.name,
        args: tc.args,
        // D77: a missing outcome (null) is a failure; D137.
        result: tc.error ?? tc.result ?? undefined,
        state: tc.success === true ? 'ok' : 'failed',
      }))}
      resources={resourceStack(cards)}
      onMentionClick={onMentionClick}
    />
  )
})

// ── Live turn: ordered activity timeline ─────────────────────────────────

/**
 * A live in-flight agent turn: the activity log reduced to one streaming
 * Message (reasoning, output, tool calls and review cards, as they occurred).
 * Before the first delta it is an empty streaming turn under the agent's name;
 * it carries `aria-busy`, never a live region (D136).
 */
function LiveTurn({
  topicId,
  threadId,
  agentId,
  name,
  workspaceId,
  onMentionClick,
}: {
  topicId: string
  threadId: string
  agentId: string
  name: string
  workspaceId?: string
  onMentionClick?: (mentionName: string) => void
}) {
  const activity = useStreamStore(
    (s) => s.activityLog[topicId]?.[agentId] ?? NO_ACTIVITY,
  )

  // Rebuilt only when this agent's log actually changes (immer structural
  // sharing keeps the array reference stable otherwise) — not on every
  // pane-level re-render.
  const segments = useMemo(
    () => buildTimeline(activity, threadId),
    [activity, threadId],
  )

  const body = segments
    .filter((s) => s.kind === 'content')
    .map((s) => s.text ?? '')
    .join('\n\n')
  const thinking = segments
    .filter((s) => s.kind === 'thinking')
    .map((s) => s.text ?? '')
    .join('\n\n')
  const cards = segments
    .filter((s) => s.kind === 'card' && s.resourceId)
    .map((s) => (
      <ResourceCard
        key={s.id}
        topicId={topicId}
        resourceId={s.resourceId ?? ''}
        fallbackTitle={s.title}
      />
    ))

  return (
    <Message
      state="streaming"
      author={{
        id: agentId,
        name,
        kind: 'agent',
        href: workspaceId
          ? ROUTES.agentProfile(workspaceId, agentId)
          : undefined,
      }}
      body={body}
      reasoning={thinking || undefined}
      toolCalls={segments
        .filter((s) => s.kind === 'tool')
        .map((seg) => ({
          id: seg.id,
          name: seg.name ?? '',
          args: seg.args,
          result: seg.error ?? seg.result,
          state: !seg.finished
            ? ('running' as const)
            : seg.success === false
              ? ('failed' as const)
              : ('ok' as const),
        }))}
      resources={resourceStack(cards)}
      onMentionClick={onMentionClick}
    />
  )
}
